import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";

/**
 * ProgressPanel — reusable rich progress indicator for long-running loads.
 *
 * Shows an active-phase card with a rotating status label, a heartbeat
 * ping so the screen never looks frozen, an elapsed-seconds counter, and
 * a segmented phase strip that lights up as phases complete. Reassurance
 * messages appear at 5 s / 10 s / 20 s waypoints.
 *
 * Callers pass an ordered `phases` list of `{icon, label, min}` where `min`
 * is the elapsed-second threshold that flips this phase to "active". The
 * default phases are dashboard-flavoured; map and data-table screens
 * override them via props.
 */
export function ProgressPanel({
  phases,
  contextLabel,             // e.g. "5 datasets selected"
  reassurances,             // { [seconds]: string } — messages appear at those elapsed thresholds
  onRetry,                  // optional retry button handler
  retryLabel = "Retry Now",
  variant = "block",        // "block" (page-level) | "overlay" (translucent card centered)
  className = "",
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const currentIdx = phases.reduce((acc, p, i) => (elapsed >= p.min ? i : acc), 0);
  const CurrentIcon = phases[currentIdx].icon;

  const reassuranceThresholds = Object.keys(reassurances || {})
    .map(Number)
    .filter((n) => elapsed >= n)
    .sort((a, b) => b - a);
  const reassuranceMsg = reassuranceThresholds.length ? reassurances[reassuranceThresholds[0]] : null;

  const cardCls =
    variant === "overlay"
      ? "bg-card px-5 py-5 rounded-lg shadow-lg border text-left max-w-sm"
      : "max-w-md mx-auto p-5 rounded-md border bg-card/60 backdrop-blur-sm";

  return (
    <div className={variant === "overlay" ? `${className}` : `p-8 space-y-6 ${className}`} data-testid="progress-panel">
      <div className={cardCls}>
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <CurrentIcon className="w-5 h-5 text-primary" />
            <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="font-medium text-sm" data-testid="progress-panel-phase">
              <Loader2 className="w-3.5 h-3.5 inline mr-1.5 animate-spin text-primary" />
              {phases[currentIdx].label}…
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {contextLabel ? `${contextLabel} · ` : ""}
              {elapsed}s elapsed
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 mt-4">
          {phases.map((p, i) => {
            const state = i < currentIdx ? "done" : i === currentIdx ? "active" : "pending";
            const cls =
              state === "done"
                ? "bg-emerald-500"
                : state === "active"
                  ? "bg-primary animate-pulse"
                  : "bg-muted-foreground/20";
            return <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${cls}`} />;
          })}
        </div>

        {reassuranceMsg && (
          <p className="text-xs text-muted-foreground/80 mt-3 leading-relaxed" data-testid="progress-panel-reassurance">
            {reassuranceMsg}
          </p>
        )}

        {onRetry && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={onRetry} data-testid="progress-panel-retry">
              {retryLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
